import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, MailX } from "lucide-react";
import { Link } from "wouter";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { trpc } from "@/lib/trpc";

type ConsentMode = "confirm" | "unsubscribe";
type ConsentState = "working" | "success" | "error";

function tokenFromLocation() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("token")?.trim() ?? "";
}

export default function CommunityNewsletterConsent({ mode }: { mode: ConsentMode }) {
  const [state, setState] = useState<ConsentState>("working");
  const started = useRef(false);
  const confirm = trpc.notifications.confirmCommunityNewsletter.useMutation();
  const unsubscribe = trpc.notifications.unsubscribeCommunityNewsletter.useMutation();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = tokenFromLocation();
    if (!token) {
      setState("error");
      return;
    }
    const mutation = mode === "confirm" ? confirm : unsubscribe;
    mutation.mutate(
      { token },
      {
        onSuccess: result => setState(result.success ? "success" : "error"),
        onError: () => setState("error"),
      },
    );
  }, [confirm, mode, unsubscribe]);

  const content =
    state === "working"
      ? {
          title: mode === "confirm" ? "Confirming your subscription" : "Updating your subscription",
          body: "This should only take a moment.",
          Icon: Loader2,
        }
      : state === "success"
        ? {
            title: mode === "confirm" ? "You’re subscribed" : "You’re unsubscribed",
            body:
              mode === "confirm"
                ? "Your email is confirmed. You’ll receive the monthly Community newsletter."
                : "You will not receive future Community newsletters.",
            Icon: CheckCircle2,
          }
        : {
            title: "This link could not be completed",
            body:
              mode === "confirm"
                ? "The confirmation link may be invalid or expired. Return to Community to request a new one."
                : "The unsubscribe link is invalid. Use the link in your most recent newsletter.",
            Icon: MailX,
          };
  const Icon = content.Icon;

  return (
    <div className="community-newsletter-consent-page">
      <Navigation />
      <main>
        <section aria-labelledby="community-newsletter-consent-title">
          <Icon
            className={state === "working" ? "is-loading" : ""}
            size={34}
            aria-hidden="true"
          />
          <p>The CHI Alliance</p>
          <h1 id="community-newsletter-consent-title">{content.title}</h1>
          <span role="status">{content.body}</span>
          {state !== "working" && (
            <Link href="/community">Return to Community</Link>
          )}
        </section>
      </main>
      <Footer />
      <style>{`
        .community-newsletter-consent-page{min-height:100vh;color:#F8FAFC;background:radial-gradient(circle at 75% 8%,rgba(220,60,145,.17),transparent 28%),#080C18}
        .community-newsletter-consent-page main{display:grid;min-height:72vh;padding:9rem 1rem 5rem;place-items:center}
        .community-newsletter-consent-page main section{width:min(100%,35rem);padding:clamp(1.8rem,5vw,3rem);border:1px solid rgba(255,255,255,.11);border-radius:1.1rem;text-align:center;background:linear-gradient(145deg,rgba(29,18,40,.96),rgba(13,13,28,.98));box-shadow:0 24px 60px rgba(0,0,0,.3)}
        .community-newsletter-consent-page main svg{color:#F9A8D4}
        .community-newsletter-consent-page main svg.is-loading{animation:community-consent-spin .8s linear infinite}
        .community-newsletter-consent-page main p{margin:1.2rem 0 .55rem;color:#F5A623;font-family:'Space Grotesk',sans-serif;font-size:.7rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
        .community-newsletter-consent-page main h1{margin:0;color:#FFFFFF;font-family:'Space Grotesk',sans-serif;font-size:clamp(1.65rem,5vw,2.5rem);letter-spacing:-.04em}
        .community-newsletter-consent-page main span{display:block;max-width:28rem;margin:1rem auto 0;color:#CBD5E1;font-family:'Inter',sans-serif;font-size:.95rem;line-height:1.65}
        .community-newsletter-consent-page main a{display:inline-flex;min-height:2.65rem;align-items:center;justify-content:center;margin-top:1.6rem;padding:.65rem 1rem;border-radius:.65rem;color:#FFFFFF;background:#DC3C91;font-family:'Inter',sans-serif;font-size:.82rem;font-weight:750;text-decoration:none}
        .community-newsletter-consent-page main a:hover,.community-newsletter-consent-page main a:focus-visible{background:#C22F7D;outline:2px solid #F9A8D4;outline-offset:3px}
        @keyframes community-consent-spin{to{transform:rotate(360deg)}}
        @media(prefers-reduced-motion:reduce){.community-newsletter-consent-page main svg.is-loading{animation:none}}
      `}</style>
    </div>
  );
}